// Country of manufacture, rendered in the reader's language.
//
// `offers.origin` is FREE TEXT typed by a supplier, mostly Chinese factories
// typing in a hurry: production holds "china", "China", "CHINA", " china ".
// The trader's offer card printed it verbatim, so an Arabic reader got
// «بلد التصنيع: china» — a lowercase Latin word mid-sentence, which reads as a
// bug even to someone who knows what it means.
//
// ── Why a map and not a title-caser ────────────────────────────────────────
// Capitalising alone would give «بلد التصنيع: China», which is tidier and still
// not Arabic. The field appears on a certificate of origin's worth of decisions,
// so the countries we actually see are worth naming properly; everything else
// falls back to a capitalised version of what the supplier typed.
//
// ── Why the fallback passes through rather than hides ──────────────────────
// The opposite call from src/lib/cityNames.js, and for the opposite reason.
// There, an unmapped ARABIC city on an English page tells its reader nothing,
// so it is dropped. Here the value is one the SUPPLIER chose to state about
// their own goods, and a country we have not mapped yet is still information
// the trader wants. Dropping it would silently remove a fact from a quote.
//
// ── Adding a country ───────────────────────────────────────────────────────
// Add all three languages or none. A half-filled row renders English to an
// Arabic reader, which is the defect this file exists to remove, one country
// at a time. src/lib/countryNames.test.js fails on a partial row.

const norm = (s) => String(s || '')
  .trim()
  .toLowerCase()
  // collapse internal runs of whitespace so "hong  kong" matches "hong kong"
  .replace(/\s+/g, ' ');

// Keyed by the NORMALISED form. Aliases are listed as separate keys rather than
// cleverly derived, because the set of ways a supplier spells their own country
// is not something to infer.
const COUNTRIES = {
  china: { ar: 'الصين', en: 'China', zh: '中国' },
  'p.r. china': { ar: 'الصين', en: 'China', zh: '中国' },
  'pr china': { ar: 'الصين', en: 'China', zh: '中国' },
  prc: { ar: 'الصين', en: 'China', zh: '中国' },
  中国: { ar: 'الصين', en: 'China', zh: '中国' },
  الصين: { ar: 'الصين', en: 'China', zh: '中国' },
  taiwan: { ar: 'تايوان', en: 'Taiwan', zh: '台湾' },
  'hong kong': { ar: 'هونغ كونغ', en: 'Hong Kong', zh: '香港' },
  vietnam: { ar: 'فيتنام', en: 'Vietnam', zh: '越南' },
  india: { ar: 'الهند', en: 'India', zh: '印度' },
  turkey: { ar: 'تركيا', en: 'Turkey', zh: '土耳其' },
  türkiye: { ar: 'تركيا', en: 'Turkey', zh: '土耳其' },
  thailand: { ar: 'تايلاند', en: 'Thailand', zh: '泰国' },
  malaysia: { ar: 'ماليزيا', en: 'Malaysia', zh: '马来西亚' },
  indonesia: { ar: 'إندونيسيا', en: 'Indonesia', zh: '印度尼西亚' },
  'south korea': { ar: 'كوريا الجنوبية', en: 'South Korea', zh: '韩国' },
  korea: { ar: 'كوريا الجنوبية', en: 'South Korea', zh: '韩国' },
  japan: { ar: 'اليابان', en: 'Japan', zh: '日本' },
  germany: { ar: 'ألمانيا', en: 'Germany', zh: '德国' },
  italy: { ar: 'إيطاليا', en: 'Italy', zh: '意大利' },
  'saudi arabia': { ar: 'السعودية', en: 'Saudi Arabia', zh: '沙特阿拉伯' },
  السعودية: { ar: 'السعودية', en: 'Saudi Arabia', zh: '沙特阿拉伯' },
  uae: { ar: 'الإمارات', en: 'United Arab Emirates', zh: '阿联酋' },
  'united arab emirates': { ar: 'الإمارات', en: 'United Arab Emirates', zh: '阿联酋' },
};

// "china" -> "China", "hong kong" -> "Hong Kong". Latin only: a non-Latin script
// has no case, and upper-casing its first codepoint is a no-op at best.
const titleCase = (raw) => raw
  .split(' ')
  .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
  .join(' ');

/**
 * The country of manufacture as the reader should see it.
 *
 * @param {string} raw   offers.origin, as the supplier typed it
 * @param {string} lang  'ar' | 'en' | 'zh'
 * @returns {string}     localized name, else a capitalised version of `raw`,
 *                       else '' for empty input
 */
export function countryOfManufacture(raw, lang) {
  const key = norm(raw);
  if (!key) return '';
  const hit = COUNTRIES[key];
  if (hit) return hit[lang] || hit.en;
  // Unmapped: keep the supplier's own word, just not shouting or mumbling.
  return titleCase(String(raw).trim().replace(/\s+/g, ' '));
}

// Exported for the test, so the map is checked against itself for completeness
// rather than against a list invented in the test file.
export const __COUNTRIES = COUNTRIES;
